import { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import theme from 'styled-theming';
import { reduxPropTypes } from '@figure/frontlib-onboarding-redux';
import { Color, Width, Error, Typography, VisuallyHidden } from '@figure/frontlib-gui';
import { stylingMode, FIGURE } from 'shared/utils/ThemeHelper';
import { Link } from 'shared/constants';
import { ToggleDropdown } from './';

const themeColors = theme('mode', {
  [FIGURE]: Color.PURPLE_RAIN,
});

const stateColors = theme('mode', {
  [FIGURE]: Color.FOCUS,
});

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: -7.5px;
  margin-right: -7.5px;
`;

const CardWrapper = styled.div`
  padding: 7.5px;
  min-width: 100%;
  flex-basis: 100%;
  margin-left: ${({ boxCenter }) => (boxCenter ? 'auto' : 'unset')};
  margin-right: ${({ boxCenter }) => (boxCenter ? 'auto' : 'unset')};

  @media print, screen and (min-width: ${Width.MD}px) {
    min-width: ${({ col }) => 100 / col}%;
    flex-basis: ${({ col }) => 100 / col}%;
  }
`;

const Card = styled.div`
  height: 100%;
  border: 1px ${({ active }) => (active ? 'solid' : 'dotted')} ${Color.GREY_3};
  text-align: ${({ center }) => (center ? 'center' : 'left')};

  display: flex;
  flex-direction: column;
`;

const CardLabel = styled.p`
  margin: 0;

  ${({ show, ...rest }) =>
    show
      ? `
    padding: 7px;
    background-color: ${themeColors(rest)};
    color: ${Color.WHITE};
    text-align: center;
  `
      : ''}
`;

const CardInner = styled.div`
  padding: 15px 22px;
  display: flex;
  justify-content: space-between;
  flex-wrap: ${({ wrapContent }) => (wrapContent ? 'wrap' : 'nowrap')};
  min-width: 100%;
  height: 100%;

  > * {
    ${({ wrapContent }) =>
      wrapContent
        ? `
      flex-basis: 100;
      min-width: 100%;
    `
        : `
      &:first-child {
        padding-right: 20px;
        max-width: 378px;
      }
    `}
  }
`;

const CardContent = styled.div`
  flex-grow: 2;
`;

const Button = styled.button`
  background: none;
  border: none;
  padding: 5px 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  appearance: none;
  align-self: flex-end;

  /* Focus styles on on keyboard focus */
  &[data-focus-visible-added]:focus {
    svg {
      box-shadow: 0 0 0 4px ${stateColors};
      border-radius: 50%;
    }
  }

  /* Hover styles are applied only when the user's primary input mechanism can hover over the component */
  @media (hover: hover) {
    &:hover {
      svg {
        box-shadow: 0 0 0 4px ${stateColors};
        border-radius: 50%;
      }
    }
  }
`;

const P = styled.p`
  min-width: 100%;
  flex-basis: 100%;
  margin: 10px 0px;
  text-align: center;
  width: 100%;
`;

const Description = styled(Typography.SmallBody)`
  flex-basis: 50%;
  padding-bottom: 1rem;
`;

const Rect = styled.rect`
  fill: ${themeColors};
`;

const LinkButton = ({ buttonText, handleClick }) => (
  <Button onClick={handleClick} data-test-id={buttonText.replace(/\s+/g, '-').toLowerCase()}>
    <VisuallyHidden>{buttonText}</VisuallyHidden>
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <Rect width="28" height="28" rx="14" />
      <rect x="8.5" y="13.3286" width="10.632" height="1.44291" fill={Color.WHITE} />
      <rect
        x="13.0571"
        y="19.1003"
        width="10.1004"
        height="1.51886"
        transform="rotate(-90 13.0571 19.1003)"
        fill={Color.WHITE}
      />
    </svg>
  </Button>
);

LinkButton.propTypes = {
  buttonText: PropTypes.string.isRequired,
  handleClick: PropTypes.func.isRequired,
};

const initialOptions = {
  [Link.TAX]: {
    id: Link.TAX,
    name: 'Link tax returns',
    description:
      'Log in to your tax preparation service or IRS.gov to provide us your gross income in a few simple steps.',
  },
  [Link.CHECKING]: {
    id: Link.CHECKING,
    name: 'Link checking',
    description:
      'Log in to your financial institution to securely link your personal checking account. Business accounts are not eligible.',
  },
  [Link.PAYSTUB]: {
    id: Link.PAYSTUB,
    name: 'Link paystubs',
    description: 'Log in to your payroll provider to provide us with your most recent paystubs.',
  },
};

const LinkList = ({
  personalLoan,
  offerPackage,
  serverError,
  handleClick,
  disabledIncomeSources,
}) => {
  const [open, setOpen] = useState(false);
  const {
    income = {},
    assetDepletion = { complete: false, investment: 0, retirement: 0, savings: 0 },
  } = personalLoan;
  const { incomePrimary } = income;
  const { statedTotalIncome = 0 } = offerPackage;
  const assetDepletionTotal =
    assetDepletion.investment + assetDepletion.retirement + assetDepletion.savings;

  // 3 cases
  let options;
  if (incomePrimary < 20000 && assetDepletionTotal > 0 && statedTotalIncome > incomePrimary) {
    // assets only
    options = [
      initialOptions[Link.CHECKING],
      initialOptions[Link.PAYSTUB],
      initialOptions[Link.TAX],
    ].map((o, i) => ({
      ...o,
      col: 1,
      hidden: i > 0,
      label: '',
      active: true,
    }));
  } else if (assetDepletionTotal > 0) {
    // assets and income
    options = [
      initialOptions[Link.TAX],
      initialOptions[Link.CHECKING],
      initialOptions[Link.PAYSTUB],
    ].map((o, i) => ({
      ...o,
      col: 2,
      hidden: false,
      label: i === 0 ? 'Required' : '',
      active: true,
    }));
  } else {
    // income only
    options = [
      initialOptions[Link.CHECKING],
      initialOptions[Link.TAX],
      initialOptions[Link.PAYSTUB],
    ].map((o, i) => ({
      ...o,
      col: 2,
      hidden: false,
      label: i === 0 ? 'Quickest option' : '',
      active: i !== 3,
    }));
  }

  const hideIndex = options.map((o) => o.hidden).indexOf(true);

  if (hideIndex > 0) options.splice(hideIndex, 0, { id: Link.OPENER });

  return (
    <>
      <Container>
        {options.map((o) => {
          if (o.hidden && !open) return null;
          if (
            disabledIncomeSources.some(
              (source) => Link.DISABLED_INCOME_SOURCES_MAP[source] === o.id
            )
          )
            return null;

          // hack for the open toggle
          if (o.id === Link.OPENER) {
            return (
              <P key={o.id}>
                Add additional sources of income
                <ToggleDropdown active={open} handleClick={() => setOpen((prev) => !prev)} />
              </P>
            );
          }

          return (
            <ThemeProvider theme={{ mode: stylingMode }} key={o.id}>
              <CardWrapper col={o.col} boxCenter={o.id === Link.PAYSTUB}>
                <Card active={o.active} center={o.col > 1}>
                  <CardLabel show={!!o.label}>{o.label}</CardLabel>
                  <CardInner wrapContent={o.col > 1}>
                    <CardContent>
                      <h3>{o.name}</h3>
                      <Description>{o.description}</Description>
                    </CardContent>
                    <LinkButton handleClick={() => handleClick(o.id)} buttonText={o.name} />
                  </CardInner>
                </Card>
              </CardWrapper>
            </ThemeProvider>
          );
        })}
      </Container>

      <Error display="block">{serverError}</Error>
    </>
  );
};

LinkList.propTypes = {
  // TODO: Fix me
  personalLoan: PropTypes.object.isRequired,
  // TODO: Fix me
  offerPackage: reduxPropTypes.offerPackage.isRequired,
  handleClick: PropTypes.func.isRequired,
  serverError: PropTypes.string.isRequired,
  disabledIncomeSources: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default LinkList;
